// 假資料層的 React 接線（§3.3：mock 狀態用 React state、reducer 與 Context）。
//
// 頁面只會用到這裡的四個 hook，永遠不會 import 任何 fixture 檔：
//   useDataSource() / useScenario() / useSession() / useList()、useRecord()
//
// ponytail: 三件事（dataSource、scenario、session）放同一個 Context，不拆三層 Provider。
// scenario 一個工作階段內幾乎不變，session 只在切換角色時變，重畫整棵樹的成本在雛形
// 完全不是問題。升級路徑：真的量到效能問題時再把 session 拆出去。

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { clearAllPersistedMockData, MockDataSource } from '@/mocks/mock-data-source';
import {
  readInjectedScenario,
  readScenarioFromSearch,
  type ScenarioInjection,
} from '@/mocks/scenario-injection';
import type {
  DataSource,
  ListQuery,
  ListResult,
  Role,
  Row,
  ScenarioState,
  Session,
} from '@/mocks/types';

// ---------------------------------------------------------------------------
// session（§3.3：存 sessionStorage，同分頁保留、關閉分頁清除）
// ---------------------------------------------------------------------------

const SESSION_STORAGE_KEY = 'eportfolio-ui.session';

/**
 * 三個角色的假登入身分。刻意不使用任何姓名樣式的字串（見 fixtures.ts 的說明），
 * 帳號用 ASCII 代號，避免被 pii-guard 判成聯絡資料。
 */
const SESSION_BY_ROLE: Readonly<Record<Role, Session>> = {
  admin: { role: 'admin', displayName: '示範管理者', loginName: 'ui-admin' },
  teacher: { role: 'teacher', displayName: '示範教師甲', loginName: 'ui-teacher' },
  student: { role: 'student', displayName: '示範學員甲', loginName: 'ui-student' },
  mentor: { role: 'mentor', displayName: '示範導師甲', loginName: 'ui-mentor' },
};

const DEFAULT_ROLE: Role = 'admin';

/**
 * 讀 sessionStorage。隱私視窗、Cookie 全擋、或 storage 配額爆掉時，
 * **存取 window.sessionStorage 這個 property 本身就會丟例外**，所以整段包在 try 裡，
 * 不是只包 getItem()。失敗一律當成「沒有既存 session」，畫面照常運作。
 */
function readStoredSession(): Session | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const role = (parsed as { role?: unknown }).role;
    return role === 'admin' || role === 'teacher' || role === 'student' || role === 'mentor'
      ? SESSION_BY_ROLE[role]
      : null;
  } catch {
    // 讀不到就當作沒有既存 session；雛形不需要區分「沒存過」與「存不進去」。
    return null;
  }
}

function writeStoredSession(session: Session): void {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // 寫不進去不影響畫面，只是重新整理後回到預設角色。
  }
}

// ---------------------------------------------------------------------------
// scenario 來源
// ---------------------------------------------------------------------------

/** 注入值優先於 URL query：Playwright 已經明確指定時，網址殘留的參數不該蓋掉它。 */
function detectInjection(): ScenarioInjection {
  const injected = readInjectedScenario();
  const fromUrl =
    typeof window === 'undefined' ? {} : readScenarioFromSearch(window.location.search);
  const result: ScenarioInjection = {};
  const scenario = injected.scenario ?? fromUrl.scenario;
  const role = injected.role ?? fromUrl.role;
  const messageKey = injected.messageKey ?? fromUrl.messageKey;
  if (scenario !== undefined) {
    result.scenario = scenario;
  }
  if (role !== undefined) {
    result.role = role;
  }
  if (messageKey !== undefined) {
    result.messageKey = messageKey;
  }
  return result;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface SessionApi {
  session: Session;
  setRole: (role: Role) => void;
}

interface MockContextValue extends SessionApi {
  dataSource: DataSource;
  scenario: ScenarioState;
  /** FC-5 R4（C1-c‴）：見 `useMessageKeyOverride()`。沒有注入時是 undefined。 */
  messageKeyOverride: string | undefined;
}

const MockContext = createContext<MockContextValue | null>(null);

export interface MockProviderProps {
  children: ReactNode;
  /** 明確指定 scenario（Storybook decorator／單元測試用），優先於注入與 URL query。 */
  scenario?: ScenarioState;
  /** 明確指定初始角色，優先於注入、URL query 與 sessionStorage。 */
  role?: Role;
  /** 覆寫 loading scenario 的延遲；測試傳 0 就不用等真的計時器。 */
  loadingDelayMs?: number;
  /**
   * FC-5 R4（C1-c‴）：明確指定 messageKey override（單元測試用），優先於注入與 URL
   * query，跟 `scenario`／`role` 走同一套優先序（同名 prop，不是新機制）。
   */
  messageKeyOverride?: string;
}

export function MockProvider({
  children,
  scenario: scenarioProp,
  role: roleProp,
  loadingDelayMs,
  messageKeyOverride: messageKeyOverrideProp,
}: MockProviderProps) {
  // 掛載時讀一次；上一頁／下一頁（popstate）時重讀。react-router 的 pushState 不會觸發
  // popstate，所以「在站內導覽時改 ?scenario=」不會生效——scenario 是整頁層級的驗證開關，
  // 切換方式就是重新載入頁面（Playwright 也是這樣用）。
  const [detected, setDetected] = useState<ScenarioInjection>(detectInjection);
  useEffect(() => {
    const handlePopState = (): void => {
      setDetected(detectInjection());
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const scenario = scenarioProp ?? detected.scenario ?? 'default';
  // FC-5 R4（C1-c‴）：同頁同動作多訊息碰撞時，用這個欄位指定要看哪一條——見
  // scenario-injection.ts 的 ScenarioInjection.messageKey 說明。
  //
  // 這個值**不**傳進 MockDataSource：confirm／failure 文字全部是 production 模板
  // 直接呼叫 `resolveLegacyMessageForAction()`／`resolveLegacyMessage()` 算出來的
  // （不是讀 `MockDataError.message`——雛形的 `processing.wrap()` 把錯誤原樣往外拋，
  // 模板 catch 區塊各自獨立重新解析要顯示哪條文字，`.message` 從未被讀取顯示），
  // 所以 override 只要能傳到模板層（`useMessageKeyOverride()`）就夠，傳進
  // DataSource 會是永遠沒人讀的死欄位。
  const messageKeyOverride = messageKeyOverrideProp ?? detected.messageKey;

  const dataSource = useMemo<DataSource>(
    () =>
      // ponytail: 這一行就是「日後接真 API 只要換一個實作」的那一行。
      new MockDataSource({
        scenario,
        ...(loadingDelayMs === undefined ? {} : { loadingDelayMs }),
      }),
    [scenario, loadingDelayMs],
  );

  const [session, setSession] = useState<Session>(() => {
    const forced = roleProp ?? detected.role;
    if (forced !== undefined) {
      return SESSION_BY_ROLE[forced];
    }
    return readStoredSession() ?? SESSION_BY_ROLE[DEFAULT_ROLE];
  });

  useEffect(() => {
    writeStoredSession(session);
  }, [session]);

  const setRole = useCallback((role: Role) => {
    setSession(SESSION_BY_ROLE[role]);
  }, []);

  const value = useMemo<MockContextValue>(
    () => ({ dataSource, scenario, session, setRole, messageKeyOverride }),
    [dataSource, scenario, session, setRole, messageKeyOverride],
  );

  return <MockContext.Provider value={value}>{children}</MockContext.Provider>;
}

function useMockContext(): MockContextValue {
  const value = useContext(MockContext);
  if (value === null) {
    throw new Error('useDataSource／useScenario／useSession 必須放在 <MockProvider> 之內');
  }
  return value;
}

/** 取得資料來源。頁面一律透過這個 hook 取資料，不直接 import fixture。 */
export function useDataSource(): DataSource {
  return useMockContext().dataSource;
}

/** 目前的 scenario。沒有 ?scenario= 也沒有注入時是 'default'。 */
export function useScenario(): ScenarioState {
  return useMockContext().scenario;
}

/** 假登入狀態與角色切換。 */
export function useSession(): SessionApi {
  const { session, setRole } = useMockContext();
  return { session, setRole };
}

/**
 * 「重置測試資料」（batch 0）：清掉所有 CRUD 異動的 localStorage 存底，讓下一次載入
 * 退回 fixtures 種子資料，並立刻重新整理頁面。
 *
 * ponytail：做法是「清 storage 後整頁 reload」，不是在原地清 React state——目前架構下
 * 一個 MockDataSource 實例自己有記憶體快取（`store`／`createdCount`），且多個頁面各自
 * 掛了 useList／useRecord，要在原地清乾淨得另外蓋一套「通知所有正在監聽的頁面重新抓
 * 資料」的事件機制。reload 一行打完收工，不為了省一次重新整理去蓋事件匯流排。
 *
 * 不放在 useMockContext 裡（不依賴目前的 dataSource/session 值），單純只是給 UI
 * 呼叫的一個動作，所以是獨立的 hook，不強迫呼叫端一定要在 MockProvider 之內
 * （雖然實務上使用情境一定在裡面）。
 */
export function useResetTestData(): () => void {
  return useCallback(() => {
    clearAllPersistedMockData();
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }, []);
}

/**
 * FC-5 R4（C1-c‴）：同頁同動作／同分類有多條 legacy 訊息互相碰撞時，Playwright／
 * URL query 注入的 `messageKey`（見 `scenario-injection.ts`）。沒有注入時是 undefined，
 * 這時所有呼叫端都該退回 R3 既有的「動作關鍵字自動挑選」，行為完全不變。
 */
export function useMessageKeyOverride(): string | undefined {
  return useMockContext().messageKeyOverride;
}

// ---------------------------------------------------------------------------
// 方便的資料 hook
// ---------------------------------------------------------------------------

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export interface ReloadableState<T> extends AsyncState<T> {
  /** 重新取一次（新增／修改／刪除之後呼叫，列表才會更新）。 */
  reload: () => void;
}

function toError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

/**
 * 讀列表。`query` 可以直接寫成 inline 物件字面值：
 * 相依性用 JSON 字串比較，不會因為每次 render 產生新物件就無限重取。
 */
export function useList(resource: string, query?: ListQuery): ReloadableState<ListResult> {
  const dataSource = useDataSource();
  const queryKey = JSON.stringify(query ?? {});
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<AsyncState<ListResult>>({
    data: null,
    loading: true,
    error: null,
  });

  // nonce 刻意放在相依性裡但不在 effect 內使用；拿掉它 reload() 就永遠不會重新取資料。
  // biome-ignore lint/correctness/useExhaustiveDependencies: nonce 是 reload() 的觸發器
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    // 從 queryKey 還原而不是直接用 query，effect 的相依性才會與實際使用的值一致
    // （否則 inline 物件會讓 lint 與實際行為對不起來）。
    const parsed = JSON.parse(queryKey) as ListQuery;
    dataSource
      .list(resource, parsed)
      .then((data) => {
        if (!cancelled) {
          setState({ data, loading: false, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setState({ data: null, loading: false, error: toError(cause) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataSource, resource, queryKey, nonce]);

  const reload = useCallback(() => {
    setNonce((current) => current + 1);
  }, []);

  return { ...state, reload };
}

/** 讀單筆。找不到時 data 為 null、error 也是 null（不是錯誤，是「查無資料」）。 */
export function useRecord(resource: string, id: string): ReloadableState<Row> {
  const dataSource = useDataSource();
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<AsyncState<Row>>({
    data: null,
    loading: true,
    error: null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: 同 useList，nonce 是 reload() 的觸發器。
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    dataSource
      .get(resource, id)
      .then((data) => {
        if (!cancelled) {
          setState({ data, loading: false, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setState({ data: null, loading: false, error: toError(cause) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataSource, resource, id, nonce]);

  const reload = useCallback(() => {
    setNonce((current) => current + 1);
  }, []);

  return { ...state, reload };
}
